import "./styles/base.css";
import { api, type Indicator, type Choropleth, type Level, type AreaDetail, type SearchHit } from "./api/client";
import { store } from "./state/store";
import { ChoroplethMap, seqColors } from "./map/choropleth";
import { openDialog } from "./ui/dialog";
import { toast } from "./ui/toast";

const ICON_SEARCH =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>';
const ICON_INFO =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 11v6"/><circle cx="12" cy="7.6" r="0.4" fill="currentColor"/></svg>';
const ICON_BACK =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>';
const THAILAND_FLAG = '<img class="breadcrumb-flag" src="/flags/thailand.svg" alt="" aria-hidden="true" />';

// ไอคอนหมวด (stroke currentColor)
const IC = {
  health:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z"/><path d="M3.5 12h4l1.5-3 2 5 1.5-2h4"/></svg>',
  vote: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 12l2 2 4-4"/><rect x="3" y="4" width="18" height="16" rx="2"/></svg>',
  people:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="8" r="3"/><path d="M3 20a6 6 0 0 1 12 0"/><path d="M16 5a3 3 0 0 1 0 6M21 20a6 6 0 0 0-4-5.7"/></svg>',
  map: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2Z"/><path d="M9 4v14M15 6v14"/></svg>',
  econ: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v18h18"/><path d="M7 14l4-4 3 3 5-6"/></svg>',
  edu: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 9 12 5 2 9l10 4 10-4Z"/><path d="M6 11v5a6 3 0 0 0 12 0v-5"/></svg>',
  folder:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z"/></svg>',
};

// เมทาของหมวด (ไอคอน + ลำดับ) — หมวดที่ยังไม่มีข้อมูลจะโชว์ "เร็วๆ นี้"
const generatedCategoryIcon = (name: string) =>
  `<img class="area-cat-icon" src="/ui-icons/${name}.png" alt="" aria-hidden="true" />`;

const CAT_META: Record<string, { icon: string; order: number; image?: string }> = {
  สุขภาพ: { icon: generatedCategoryIcon("security"), order: 1, image: "health" },
  การเลือกตั้ง: { icon: generatedCategoryIcon("public-service"), order: 2, image: "election" },
  ประชากร: { icon: generatedCategoryIcon("population"), order: 3, image: "population" },
  เศรษฐกิจ: { icon: generatedCategoryIcon("data-search"), order: 4, image: "economy" },
  การศึกษา: { icon: generatedCategoryIcon("documents"), order: 5, image: "education" },
  ภูมิศาสตร์: { icon: generatedCategoryIcon("map-location"), order: 6, image: "geography" },
};
const AREA_INDICATOR_CODE = "area_km2";

const app = document.getElementById("app")!;

app.innerHTML = `
  <header class="app-header">
    <a class="brand" href="/" title="สถิติ — กลับหน้าหลัก">
      <span class="brand-mark" aria-hidden="true"><img src="/brand/stat-mark-cutout.png?v=20260723" alt="" /></span>
      <span class="brand-text">
        <span class="brand-name">สถิติ</span>
        <span class="brand-tag">สถิติข้อมูลประเทศไทย</span>
      </span>
    </a>
    <span class="header-divider" aria-hidden="true"></span>
    <div class="header-controls" role="group" aria-label="ชุดข้อมูลที่กำลังดู">
      <span class="select-wrap select-wrap--dataset">
        <select id="indicator" class="select" aria-label="เลือกชุดข้อมูล"></select>
      </span>
      <span class="select-wrap select-wrap--year">
        <select id="year" class="select" aria-label="เลือกปี"></select>
      </span>
    </div>
    <div class="header-tools">
      <div class="field search-field">
        ${ICON_SEARCH}
        <input id="search" class="input" type="search" placeholder="ค้นหาจังหวัด/อำเภอ/ตำบล…" autocomplete="off" role="combobox" aria-autocomplete="list" aria-expanded="false" aria-controls="search-results" />
        <div id="search-results" class="search-results" role="listbox" aria-label="ผลการค้นหาพื้นที่"></div>
      </div>
      <button id="about-btn" type="button" class="btn btn-ghost btn-icon" aria-label="เกี่ยวกับเว็บนี้" title="เกี่ยวกับ สถิติข้อมูลประเทศไทย">${ICON_INFO}</button>
    </div>
  </header>
  <main class="app-main" id="main-layout">
    <aside class="region-nav region-nav--left" id="region-nav-left" aria-label="รายชื่อจังหวัดตามภูมิภาค"></aside>
    <section class="map-pane" id="map-pane" aria-label="แผนที่">
      <div class="map-ambience" aria-hidden="true">
        <picture>
          <source media="(min-width: 1700px) and (min-aspect-ratio: 2/1)" srcset="/map-bg/thailand-ambience-ultrawide.png" />
          <source media="(min-width: 1200px) and (min-aspect-ratio: 16/10)" srcset="/map-bg/thailand-ambience-wide.png" />
          <source media="(min-width: 721px) and (min-aspect-ratio: 1/1)" srcset="/map-bg/thailand-ambience-standard.png" />
          <source media="(min-width: 721px)" srcset="/map-bg/thailand-ambience-tall.png" />
          <img src="/map-bg/thailand-ambience-mobile.png" alt="" />
        </picture>
        <img class="map-ambience-region" id="map-region-background" alt="" />
      </div>
      <div class="population-ticker" id="population-ticker" hidden aria-live="polite" aria-label="ตัวเลขประชากรแบบจำลอง">
        <strong id="population-total">—</strong>
        <span class="population-signal is-up" id="population-signal" aria-hidden="true">
          <b>▲</b><span id="population-signal-value">+12</span>
        </span>
      </div>
      <nav class="breadcrumb" id="breadcrumb" aria-label="เส้นทางการดู"></nav>
      <div class="map-loading" id="map-loading" aria-hidden="true">
        <div class="loading-box"><div class="spinner lg"></div><span>กำลังโหลดแผนที่…</span></div>
      </div>
    </section>
    <aside class="side-pane" id="side-pane">
      <div id="summary"></div>
      <div id="ranking" class="card"></div>
      <div id="legend" class="legend card"></div>
    </aside>
    <aside class="region-nav region-nav--right" id="region-nav-right" aria-label="รายชื่อจังหวัดภาคตะวันออกเฉียงเหนือ ภาคตะวันออก และภาคใต้"></aside>
  </main>
  <footer class="app-footer">
    <span id="footsrc">แหล่งข้อมูล: —</span>
    <span style="flex:1"></span>
    <span>© m1n.app</span>
    <span id="status" class="sr-only" aria-live="polite"></span>
  </footer>
`;

const mapPane = document.getElementById("map-pane")!;
const mapRegionBackgroundEl = document.getElementById("map-region-background") as HTMLImageElement;
const mainLayoutEl = document.getElementById("main-layout")!;
const regionNavLeftEl = document.getElementById("region-nav-left")!;
const regionNavRightEl = document.getElementById("region-nav-right")!;
const indicatorSelect = document.getElementById("indicator") as HTMLSelectElement;
const yearSelect = document.getElementById("year") as HTMLSelectElement;
const headerControlsEl = document.querySelector<HTMLElement>(".header-controls")!;
const headerDividerEl = document.querySelector<HTMLElement>(".header-divider")!;
const populationTickerEl = document.getElementById("population-ticker")!;
const populationTotalEl = document.getElementById("population-total")!;
const populationSignalEl = document.getElementById("population-signal")!;
const populationSignalValueEl = document.getElementById("population-signal-value")!;
const searchInput = document.getElementById("search") as HTMLInputElement;
const searchResultsEl = document.getElementById("search-results")!;
const statusEl = document.getElementById("status")!;
const breadcrumbEl = document.getElementById("breadcrumb")!;
const legendEl = document.getElementById("legend")!;
const summaryEl = document.getElementById("summary")!;
const rankingEl = document.getElementById("ranking")!;
const loadingEl = document.getElementById("map-loading")!;
const footsrcEl = document.getElementById("footsrc")!;

let rankOrder: "desc" | "asc" = "desc";
let renderVersion = 0;

const map = new ChoroplethMap(mapPane);
let indicators: Indicator[] = [];
const areaNames: Record<string, string> = {}; // area_code → name_th (สะสมทุกระดับที่ผ่าน)

const LEVEL_NOUN: Record<Level, string> = { province: "จังหวัด", amphoe: "อำเภอ", tambon: "ตำบล" };
const LEVEL_FULL: Record<Level, string> = { province: "จังหวัด", amphoe: "อำเภอ/เขต", tambon: "ตำบล/แขวง" };

const REGION_NAVIGATION = {
  left: [
    {
      name: "ภาคเหนือ",
      provinces: [
        ["50", "เชียงใหม่"], ["57", "เชียงราย"], ["52", "ลำปาง"], ["51", "ลำพูน"], ["58", "แม่ฮ่องสอน"],
        ["55", "น่าน"], ["56", "พะเยา"], ["54", "แพร่"], ["53", "อุตรดิตถ์"], ["63", "ตาก"],
        ["64", "สุโขทัย"], ["65", "พิษณุโลก"], ["66", "พิจิตร"], ["67", "เพชรบูรณ์"], ["60", "นครสวรรค์"],
        ["61", "อุทัยธานี"], ["62", "กำแพงเพชร"],
      ],
    },
    {
      name: "ภาคกลาง",
      provinces: [
        ["10", "กรุงเทพฯ"], ["11", "สมุทรปราการ"], ["12", "นนทบุรี"], ["13", "ปทุมธานี"], ["14", "อยุธยา"],
        ["15", "อ่างทอง"], ["16", "ลพบุรี"], ["17", "สิงห์บุรี"], ["18", "ชัยนาท"], ["19", "สระบุรี"],
        ["26", "นครนายก"], ["72", "สุพรรณบุรี"], ["73", "นครปฐม"], ["74", "สมุทรสาคร"], ["75", "สมุทรสงคราม"],
      ],
    },
    {
      name: "ภาคตะวันตก",
      provinces: [["71", "กาญจนบุรี"], ["70", "ราชบุรี"], ["76", "เพชรบุรี"], ["77", "ประจวบคีรีขันธ์"]],
    },
  ],
  right: [
    {
      name: "ภาคตะวันออกเฉียงเหนือ",
      provinces: [
        ["30", "นครราชสีมา"], ["31", "บุรีรัมย์"], ["32", "สุรินทร์"], ["33", "ศรีสะเกษ"], ["34", "อุบลราชธานี"],
        ["35", "ยโสธร"], ["36", "ชัยภูมิ"], ["37", "อำนาจเจริญ"], ["38", "บึงกาฬ"], ["39", "หนองบัวลำภู"],
        ["40", "ขอนแก่น"], ["41", "อุดรธานี"], ["42", "เลย"], ["43", "หนองคาย"], ["44", "มหาสารคาม"],
        ["45", "ร้อยเอ็ด"], ["46", "กาฬสินธุ์"], ["47", "สกลนคร"], ["48", "นครพนม"], ["49", "มุกดาหาร"],
      ],
    },
    {
      name: "ภาคตะวันออก",
      provinces: [["20", "ชลบุรี"], ["21", "ระยอง"], ["22", "จันทบุรี"], ["23", "ตราด"], ["24", "ฉะเชิงเทรา"], ["25", "ปราจีนบุรี"], ["27", "สระแก้ว"]],
    },
    {
      name: "ภาคใต้",
      provinces: [
        ["80", "นครศรีธรรมราช"], ["81", "กระบี่"], ["82", "พังงา"], ["83", "ภูเก็ต"], ["84", "สุราษฎร์ธานี"],
        ["85", "ระนอง"], ["86", "ชุมพร"], ["90", "สงขลา"], ["91", "สตูล"], ["92", "ตรัง"], ["93", "พัทลุง"],
        ["94", "ปัตตานี"], ["95", "ยะลา"], ["96", "นราธิวาส"],
      ],
    },
  ],
} as const;

const REGION_CARD_ART: Record<string, string> = {
  ภาคเหนือ: "north",
  ภาคกลาง: "central",
  ภาคตะวันตก: "west",
  ภาคตะวันออกเฉียงเหนือ: "northeast",
  ภาคตะวันออก: "east",
  ภาคใต้: "south",
};

function renderRegionNavigation(container: HTMLElement, groups: readonly { name: string; provinces: readonly (readonly [string, string])[] }[]) {
  container.innerHTML = groups
    .map(
      (group) => {
        const art = REGION_CARD_ART[group.name];
        return `<section class="region-card${art ? " has-art" : ""}"${art ? ` style="--region-image:url('/region-bg/${art}.png')"` : ""}>
        <h2>${group.name}<span>${group.provinces.length}</span></h2>
        <div class="region-province-list">
          ${group.provinces
            .map(([code, name]) => `<button type="button" data-code="${code}" title="ดูข้อมูลจังหวัด${name}">${name}</button>`)
            .join("")}
        </div>
      </section>`;
      },
    )
    .join("");
  container.querySelectorAll<HTMLButtonElement>("button[data-code]").forEach((button) => {
    button.addEventListener("click", () => store.set({ level: "amphoe", area: button.dataset.code! }));
  });
}

renderRegionNavigation(regionNavLeftEl, [
  REGION_NAVIGATION.left[1],  // กลาง
  REGION_NAVIGATION.left[0],  // เหนือ
  REGION_NAVIGATION.right[0], // อีสาน
  REGION_NAVIGATION.right[2], // ใต้
  REGION_NAVIGATION.right[1], // ตะวันออก
  REGION_NAVIGATION.left[2],  // ตะวันตก
]);
renderRegionNavigation(regionNavRightEl, []);

function currentIndicator(): Indicator | undefined {
  const code = store.get().indicator;
  return indicators.find((i) => i.code === code);
}

// ข้อมูล indicator มาจาก API/ฐานข้อมูล จึงต้อง escape ก่อนนำไปประกอบเป็น HTML string
function escapeHTML(value: string): string {
  return value.replace(/[&<>'"]/g, (char) => {
    const entities: Record<string, string> = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      "'": "&#39;",
      '"': "&quot;",
    };
    return entities[char];
  });
}

function nextLevel(level: Level): Level | null {
  if (level === "province") return "amphoe";
  if (level === "amphoe") return "tambon";
  return null;
}

function supportsLevel(indicator: Indicator | undefined, level: Level): boolean {
  return !!indicator?.levels?.includes(level);
}

function makeFormatter(ind?: Indicator): (v: number) => string {
  const nf = new Intl.NumberFormat("th-TH", {
    maximumFractionDigits: ind?.decimals ?? 0,
    minimumFractionDigits: 0,
  });
  const unit = ind?.unit ? ` ${ind.unit}` : "";
  return (v: number) => nf.format(v) + unit;
}

async function loadIndicators() {
  indicators = await api.indicators();
  void loadPopulationTicker();
  indicatorSelect.innerHTML = "";
  indicatorSelect.add(new Option("— เลือกชุดข้อมูล —", "")); // placeholder (landing)
  for (const [cat, items] of groupByCategory(indicators)) {
    const og = document.createElement("optgroup");
    og.label = cat;
    for (const it of items) og.appendChild(new Option(it.name_th, it.code));
    indicatorSelect.appendChild(og);
  }
  // ไม่ auto-select — เข้ามาครั้งแรกเห็นแผนที่เปล่า + catalog (นอกจาก URL มี indicator อยู่)
  const cur = store.get();
  const selected = indicators.find((i) => i.code === cur.indicator);
  if (selected) {
    indicatorSelect.value = selected.code;
    // deep link ที่ไม่มี/มีปีไม่ถูกต้อง ให้เปิดปีล่าสุดแทน landing ที่ state ค้าง
    if (!cur.period || !selected.years?.includes(cur.period)) {
      store.set({ period: latestYear(selected) });
    }
  }
}

function animatePopulationTotal(total: number, onComplete?: () => void) {
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reducedMotion) {
    populationTotalEl.textContent = new Intl.NumberFormat("th-TH").format(total);
    onComplete?.();
    return;
  }
  const start = performance.now();
  const duration = 1400;
  const nf = new Intl.NumberFormat("th-TH");
  const tick = (now: number) => {
    const progress = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - progress, 3);
    populationTotalEl.textContent = nf.format(Math.round(total * eased));
    if (progress < 1) requestAnimationFrame(tick);
    else onComplete?.();
  };
  requestAnimationFrame(tick);
}

function updatePopulationTicker(current: number): number {
  const up = Math.random() >= 0.42;
  const amount = Math.floor(Math.random() * 37) + 3;
  const next = current + (up ? amount : -amount);
  populationSignalEl.classList.toggle("is-up", up);
  populationSignalEl.classList.toggle("is-down", !up);
  populationSignalEl.querySelector("b")!.textContent = up ? "▲" : "▼";
  populationSignalValueEl.textContent = `${up ? "+" : "−"}${amount}`;
  populationTotalEl.textContent = new Intl.NumberFormat("th-TH").format(next);
  populationTickerEl.animate(
    [{ transform: "translateY(3px)", opacity: 0.55 }, { transform: "translateY(0)", opacity: 1 }],
    { duration: 360, easing: "cubic-bezier(.2,.6,.2,1)" },
  );
  return next;
}

async function loadPopulationTicker() {
  const population = indicators.find((indicator) => indicator.code === "population");
  const year = population?.years?.length ? Math.max(...population.years) : undefined;
  if (!year) return;
  try {
    const choro = await api.choropleth(population!.code, "province", year);
    const total = Object.values(choro.values).reduce((sum, value) => sum + value, 0);
    if (!Number.isFinite(total) || total <= 0) return;
    populationTickerEl.hidden = false;
    let displayedTotal = Math.round(total);
    animatePopulationTotal(displayedTotal, () => {
      window.setInterval(() => {
        displayedTotal = updatePopulationTicker(displayedTotal);
      }, 1200);
    });
  } catch {
    // ไม่รบกวนผู้ใช้หากตัวชี้วัดประชากรยังโหลดไม่ได้
  }
}

// จัดกลุ่ม indicator ตามหมวด เรียงตามลำดับใน CAT_META
function groupByCategory(list: Indicator[]): Map<string, Indicator[]> {
  const m = new Map<string, Indicator[]>();
  for (const it of list) {
    const c = it.category || "อื่นๆ";
    if (!m.has(c)) m.set(c, []);
    m.get(c)!.push(it);
  }
  return new Map([...m.entries()].sort((a, b) => (CAT_META[a[0]]?.order ?? 99) - (CAT_META[b[0]]?.order ?? 99)));
}


function selectDataset(code: string) {
  const ind = indicators.find((i) => i.code === code);
  if (!ind) return;
  store.set({ indicator: code, period: latestYear(ind), level: "province", area: null });
}

function populateYears() {
  const ind = currentIndicator();
  const s = store.get();
  yearSelect.innerHTML = "";
  if (!ind || !ind.years?.length) {
    yearSelect.disabled = true;
    return;
  }
  yearSelect.disabled = false;
  for (const y of [...ind.years].sort((a, b) => b - a)) yearSelect.add(new Option(`ปี ${y}`, String(y)));
  if (s.period) yearSelect.value = String(s.period);
}

// โหมดการแสดงผล 3 แบบ:
//   national  = หน้าแรก: แผนที่ + ข้อมูลพื้นฐาน (พื้นที่) ไม่มีแผงข้าง ไม่มีหมวดให้เลือก
//   area      = เลือกจังหวัด/อำเภอแล้ว: แผงข้างโชว์ข้อมูลย่อยของพื้นที่นั้น
//   dataset   = เลือกชุดข้อมูลแล้ว: choropleth + summary/ranking/legend
function setLandingMode(on: boolean, national: boolean) {
  mainLayoutEl.classList.toggle("is-landing", on);
  mainLayoutEl.classList.toggle("is-national", on && national);
  regionNavLeftEl.setAttribute("aria-hidden", String(!on));
  regionNavRightEl.setAttribute("aria-hidden", "true");
  // หน้าแรกระดับประเทศ = ไม่มีแผงข้าง (แผนที่เต็มพื้นที่)
  summaryEl.style.display = on && national ? "none" : "";
  // หน้าแรก = เหลือแค่กล่องค้นหา (ซ่อนตัวเลือกชุดข้อมูล/ปี) — จะโผล่เมื่อเข้าไปในพื้นที่
  headerControlsEl.style.display = on && national ? "none" : "";
  headerDividerEl.style.display = on && national ? "none" : "";
  legendEl.style.display = on ? "none" : "";
  rankingEl.style.display = on ? "none" : "";
  yearSelect.style.display = on ? "none" : "";
}

function regionalBackgroundForProvince(provinceCode: string): string | null {
  const code = Number(provinceCode);
  if ((code >= 50 && code <= 58) || (code >= 60 && code <= 67) || code === 70 || code === 71 || code === 76 || code === 77)
    return "/map-bg/north-mountains.png";
  if (code >= 30 && code <= 49) return "/map-bg/isan-agriculture.png";
  if ((code >= 10 && code <= 19) || code === 26 || (code >= 72 && code <= 75)) return "/map-bg/central-city.png";
  if ((code >= 20 && code <= 25) || code === 27 || (code >= 80 && code <= 86) || (code >= 90 && code <= 96))
    return "/map-bg/south-sea.png";
  return null;
}

function setRegionalMapBackground(src: string | null) {
  mapPane.classList.toggle("has-regional-background", Boolean(src));
  if (src && mapRegionBackgroundEl.getAttribute("src") !== src) mapRegionBackgroundEl.src = src;
  if (!src) mapRegionBackgroundEl.removeAttribute("src");
}

async function renderBlankMap(s: { level: Level; area: string | null }, isCurrent: () => boolean) {
  try {
    const cfg = viewConfig(s);
    const areaIndicator = indicators.find((indicator) => indicator.code === AREA_INDICATOR_CODE);
    const areaYear = areaIndicator?.years?.length ? Math.max(...areaIndicator.years) : null;
    const [topo, areaChoro] = await Promise.all([
      fetch(cfg.geoURL).then((r) => (r.ok ? r.json() : Promise.reject(new Error("โหลด geo ไม่ได้")))),
      areaYear
        ? api.choropleth(AREA_INDICATOR_CODE, cfg.level, areaYear).catch(() => ({ meta: { min: 0, max: 0 }, values: {} }))
        : Promise.resolve({ meta: { min: 0, max: 0 }, values: {} }),
    ]);
    if (!isCurrent()) return;
    const objName = Object.keys(topo.objects)[0];
    for (const g of topo.objects[objName].geometries) {
      const p = g.properties || {};
      if (p.area_code) areaNames[p.area_code] = p.name_th;
    }
    const isSubdivision = cfg.level !== "province";
    const regionalBackground = isSubdivision && s.area ? regionalBackgroundForProvince(s.area.slice(0, 2)) : null;
    mapPane.classList.toggle("has-landing-background", !isSubdivision);
    setRegionalMapBackground(regionalBackground);
    const areaFormatter = new Intl.NumberFormat("th-TH", { maximumFractionDigits: 2 });
    map.render(topo, objName, {
      values: areaChoro.values,
      filterParent: cfg.filterParent,
      formatValue: (v) => `พื้นที่ ${areaFormatter.format(v)} ตร.กม.`,
      // landing ใช้ภาพรายจังหวัดเป็น texture ภายในขอบเขตจังหวัด;
      // ภาพภูมิภาค 4 ชุดยังเห็นผ่าน texture เล็กน้อย เพื่อรวม mood ของทั้งแผนที่
      backgroundImage: isSubdivision ? undefined : (areaCode) => `/province-bg/${areaCode}.png`,
      backgroundImageOpacity: isSubdivision ? undefined : 0.84,
      showAreaLabels: true,
      landingAmphoe: isSubdivision,
      regionalBackground: Boolean(regionalBackground),
      reliefEdges: Boolean(s.area),
      showOuterBoundary: !isSubdivision,
      // โหมดสำรวจ: เจาะลึกได้ทุกระดับ (จังหวัด→อำเภอ→ตำบล) เพื่อดูสถิติของพื้นที่ย่อย
      onSelect: (areaCode) => navigateArea(areaCode, cfg.level),
    });
  } catch {
    /* ignore */
  }
}

function latestYear(it: Indicator): number | null {
  return it.years && it.years.length ? Math.max(...it.years) : null;
}

function setLoading(on: boolean) {
  loadingEl.classList.toggle("active", on);
  loadingEl.setAttribute("aria-hidden", String(!on));
}

// เลือก geo/level/filter ตามสถานะ (3 ชั้น: จังหวัด→อำเภอ→ตำบล)
function viewConfig(s: { level: Level; area: string | null }): {
  geoURL: string;
  level: Level;
  filterParent?: string;
} {
  if (s.level === "amphoe" && s.area)
    return { geoURL: "/geo/amphoes.topojson", level: "amphoe", filterParent: s.area };
  if (s.level === "tambon" && s.area)
    return { geoURL: `/geo/tambons/${s.area.slice(0, 2)}.topojson`, level: "tambon", filterParent: s.area };
  return { geoURL: "/geo/thailand.topojson", level: "province" };
}

async function renderView() {
  const version = ++renderVersion;
  const isCurrent = () => version === renderVersion;
  const s = store.get();
  // sync header controls กับ state
  indicatorSelect.value = s.indicator ?? "";
  populateYears();

  const hasDataset = !!s.indicator && !!s.period;
  const national = s.level === "province" && !s.area;
  setLandingMode(!hasDataset, national);

  // หน้าแรก/สำรวจพื้นที่: แผนที่ + ข้อมูลพื้นฐาน (ไม่มีหมวดให้เลือก)
  if (!hasDataset) {
    if (national) breadcrumbEl.innerHTML = `<span class="here">${THAILAND_FLAG}ประเทศไทย</span>`;
    else renderBreadcrumb();
    statusEl.textContent = national
      ? "คลิกจังหวัดเพื่อดูข้อมูลของพื้นที่"
      : `${areaNames[s.area!] ?? ""} · คลิกพื้นที่ย่อยเพื่อเจาะลึก`;
    footsrcEl.textContent = "เว็บข้อมูลเปิด (open data) ประเทศไทย · แสดงผลรายพื้นที่";
    setLoading(true);
    await renderBlankMap(s, isCurrent);
    if (!isCurrent()) return;
    setLoading(false);
    // เลือกพื้นที่แล้ว → โชว์ข้อมูลย่อยของพื้นที่นั้นในแผงข้าง
    if (!national && s.area) renderAreaPanel(s.area, isCurrent);
    return;
  }

  const cfg = viewConfig(s);
  const level = cfg.level;
  mapPane.classList.remove("has-landing-background");
  setRegionalMapBackground(null);

  setLoading(true);
  try {
    const topo = await fetch(cfg.geoURL).then((r) =>
      r.ok ? r.json() : Promise.reject(new Error("โหลด geo ไม่ได้")),
    );
    if (!isCurrent()) return;

    // สะสมชื่อพื้นที่จาก topo (ไว้ทำ breadcrumb/dialog) ทุกระดับ
    const objName = Object.keys(topo.objects)[0];
    for (const g of topo.objects[objName].geometries) {
      const p = g.properties || {};
      if (p.area_code) areaNames[p.area_code] = p.name_th;
    }

    // choropleth: ถ้า indicator ไม่มีข้อมูลระดับนี้ → grey (values ว่าง)
    let choro: Choropleth = { meta: { min: 0, max: 0 }, values: {} };
    try {
      choro = await api.choropleth(s.indicator!, level, s.period!, s.classify);
    } catch {
      /* no data at this level → render outlines only */
    }
    if (!isCurrent()) return;

    const ind = currentIndicator();
    const fmt = makeFormatter(ind);
    const drillLevel = nextLevel(level);
    const canDrill = drillLevel ? supportsLevel(ind, drillLevel) : true;
    map.render(topo, objName, {
      values: choro.values,
      bins: choro.meta.bins,
      selected: null,
      filterParent: cfg.filterParent,
      formatValue: fmt,
      showOuterBoundary: level === "province",
      reliefEdges: Boolean(s.area),
      onSelect: canDrill ? (code) => navigateArea(code, level) : undefined,
    });

    renderBreadcrumb();
    renderSummary(choro, level);
    renderLegend(choro, fmt);
    renderRanking(level, choro.meta.bins ?? [], version);
    if (ind) footsrcEl.textContent = `ตัวชี้วัด: ${ind.name_th}${ind.unit ? ` (${ind.unit})` : ""}`;
    const n = Object.keys(choro.values).length;
    statusEl.textContent =
      level === "province"
        ? `${n} จังหวัด · ปี ${s.period}`
        : `${areaNames[s.area!] ?? ""} · ราย${LEVEL_NOUN[level]} ${n} พื้นที่ · ปี ${s.period}`;
  } catch (e) {
    if (!isCurrent()) return;
    mapPane.querySelectorAll("svg,.map-error").forEach((el) => el.remove());
    const div = document.createElement("div");
    div.className = "map-error empty-state";
    div.innerHTML = `<img class="empty-illustration" src="/ui-illustrations/empty-map.png" alt="" aria-hidden="true" /><p>โหลดแผนที่ไม่ได้: ${(e as Error).message}</p>`;
    mapPane.appendChild(div);
    toast("โหลดแผนที่ไม่ได้ กรุณาลองใหม่อีกครั้ง", "error");
  } finally {
    if (isCurrent()) setLoading(false);
  }
}

// นำทางเมื่อคลิกพื้นที่ (แผนที่หรือแถบอันดับ): จังหวัด→อำเภอ→ตำบล→dialog
function navigateArea(code: string, level: Level) {
  const targetLevel = nextLevel(level);
  const ind = currentIndicator();
  // เตือนเฉพาะตอนเลือกชุดข้อมูลแล้ว — โหมดสำรวจ (ยังไม่เลือกชุด) ต้องเจาะลึกได้เสมอ
  if (ind && targetLevel && !supportsLevel(ind, targetLevel)) {
    toast(`ชุดข้อมูลนี้ไม่มีข้อมูลราย${LEVEL_NOUN[targetLevel]}`, "error");
    return;
  }
  if (level === "province") store.set({ level: "amphoe", area: code });
  else if (level === "amphoe") store.set({ level: "tambon", area: code });
  else openAreaDialog(code);
}

// แผงข้อมูลพื้นที่สำหรับการสำรวจโดยยังไม่เลือกชุดข้อมูล
async function renderAreaPanel(code: string, isCurrent: () => boolean) {
  summaryEl.innerHTML = `<div class="card"><div class="loading-box" style="padding:16px"><div class="spinner"></div><span>กำลังโหลดข้อมูลพื้นที่…</span></div></div>`;
  try {
    const d = await api.area(code);
    if (!isCurrent()) return;

    // จัดกลุ่มตัวชี้วัดตามหมวด (หมวดดึงจากรายการ indicators ที่โหลดไว้แล้ว)
    const byCat = new Map<string, typeof d.metrics>();
    for (const m of d.metrics) {
      const cat = indicators.find((i) => i.code === m.code)?.category || "อื่นๆ";
      if (!byCat.has(cat)) byCat.set(cat, []);
      byCat.get(cat)!.push(m);
    }
    const cats = [...byCat.keys()].sort(
      (a, b) => (CAT_META[a]?.order ?? 99) - (CAT_META[b]?.order ?? 99),
    );

    const groups = cats
      .map((cat) => {
        const icon = CAT_META[cat]?.icon ?? IC.folder;
        const list = byCat.get(cat)!;
        // ปีย้ายมาไว้ที่หัวหมวด (เดิมซ้ำทุกตัวชี้วัด = เปลืองที่)
        const years = [...new Set(list.map((m) => m.year_be))].sort();
        const yearLabel = years.length === 1 ? `ปี ${years[0]}` : `ปี ${years[0]}–${years[years.length - 1]}`;
        const rows = list
          .map((m) => {
            const nf = new Intl.NumberFormat("th-TH", { maximumFractionDigits: m.decimals });
            // แถบ = ตำแหน่งเทียบพื้นที่ระดับเดียวกัน (ยาว = ค่าสูง/อันดับดี)
            const pct = m.of > 1 ? ((m.of - m.rank) / (m.of - 1)) * 100 : 100;
            return `
            <div class="area-metric" title="${escapeHTML(m.name_th)} — อันดับ ${m.rank} จาก ${m.of}">
              <div class="area-metric-top">
                <span class="area-metric-name">${escapeHTML(m.name_th)}</span>
                <span class="area-metric-val">${nf.format(m.value)}<span class="area-metric-unit">${escapeHTML(m.unit)}</span></span>
              </div>
              <div class="area-metric-bottom">
                <span class="m-bar"><span style="width:${pct.toFixed(0)}%"></span></span>
                <span class="area-rank">${m.rank}<i>/${m.of}</i></span>
              </div>
            </div>`;
          })
          .join("");
        return `<section class="area-cat">
          <h3 class="area-cat-head">${icon}<span>${escapeHTML(cat)}</span><em>${yearLabel}</em></h3>
          <div class="area-metric-grid">${rows}</div>
        </section>`;
      })
      .join("");

    const sub = [
      LEVEL_FULL[d.area.level],
      d.area.parent_name ? escapeHTML(d.area.parent_name) : "",
      d.children ? `${new Intl.NumberFormat("th-TH").format(d.children)} พื้นที่ย่อย` : "",
    ]
      .filter(Boolean)
      .join(" · ");

    summaryEl.innerHTML = `
      <div class="card area-panel">
        <div class="area-panel-head">
          <h2 class="area-panel-name">${escapeHTML(d.area.name_th)}</h2>
          <p class="muted">${sub}</p>
        </div>
        ${groups || `<div class="area-no-data"><img src="/ui-illustrations/no-data-location.png" alt="" aria-hidden="true" /><p class="muted">ยังไม่มีข้อมูลตัวชี้วัดสำหรับพื้นที่นี้</p></div>`}
      </div>`;
  } catch {
    if (isCurrent()) summaryEl.innerHTML = `<div class="card"><p class="muted">โหลดข้อมูลพื้นที่ไม่ได้</p></div>`;
  }
}

// สีบาร์ = สเกลเดียวกับแผนที่ (bins + map-seq) เพื่อให้สอดคล้องกัน
function colorFor(v: number, bins: number[], colors: string[]): string {
  if (!bins.length) return colors[colors.length - 1] ?? "";
  let idx = 0;
  for (const b of bins) {
    if (v >= b) idx++;
    else break;
  }
  return colors[Math.min(idx, colors.length - 1)] ?? "";
}

// แถบอันดับ (magnitude) — top N ของระดับปัจจุบัน (ในกลุ่มพี่น้องถ้า drill อยู่)
async function renderRanking(level: Level, bins: number[], version = renderVersion) {
  const s = store.get();
  if (!s.indicator || !s.period) return;
  const parent = level === "province" ? undefined : (s.area ?? undefined);
  try {
    const rk = await api.ranking(s.indicator, level, s.period, { parent, order: rankOrder, limit: 12 });
    if (version !== renderVersion) return;
    if (!rk.items.length) {
      rankingEl.style.display = "none";
      return;
    }
    rankingEl.style.display = "";
    const colors = seqColors();
    const maxV = Math.max(...rk.items.map((i) => Math.abs(i.value))) || 1;
    const nf = new Intl.NumberFormat("th-TH", { maximumFractionDigits: rk.meta.decimals });
    const rows = rk.items
      .map((it) => {
        const w = Math.max(4, (Math.abs(it.value) / maxV) * 100);
        const col = colorFor(it.value, bins, colors);
        const sel = it.area_code === s.area ? " is-selected" : "";
        return `
        <div class="rank-row${sel}" role="button" tabindex="0" data-code="${it.area_code}" title="${it.name_th}">
          <span class="rank-no">${it.rank}</span>
          <span class="rank-body">
            <span class="rank-label"><span class="rank-name">${it.name_th}</span><span class="rank-val">${nf.format(it.value)}</span></span>
            <span class="rank-track"><span style="width:${w.toFixed(1)}%;background:${col}"></span></span>
          </span>
        </div>`;
      })
      .join("");
    const scope = parent ? `ใน${areaNames[parent] ?? ""}` : "ทั้งประเทศ";
    rankingEl.innerHTML = `
      <div class="rank-head">
        <span class="rank-title">อันดับ${LEVEL_NOUN[level]} · ${scope}</span>
        <span class="rank-toggle">
          <button type="button" data-ord="desc" class="${rankOrder === "desc" ? "on" : ""}">มากสุด</button>
          <button type="button" data-ord="asc" class="${rankOrder === "asc" ? "on" : ""}">น้อยสุด</button>
        </span>
      </div>
      <div class="rank-list">${rows}</div>`;
    rankingEl.querySelectorAll<HTMLElement>(".rank-toggle button").forEach((b) =>
      b.addEventListener("click", () => {
        const o = b.dataset.ord as "desc" | "asc";
        if (o !== rankOrder) {
          rankOrder = o;
          renderRanking(level, bins, version);
        }
      }),
    );
    rankingEl.querySelectorAll<HTMLElement>(".rank-row").forEach((row) => {
      const go = () => navigateArea(row.dataset.code!, level);
      row.addEventListener("click", go);
      row.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          go();
        }
      });
    });
  } catch {
    if (version !== renderVersion) return;
    rankingEl.style.display = "none";
  }
}

// dialog รายละเอียดพื้นที่ + อันดับเทียบพื้นที่พี่น้อง (ข้อมูลจริงจาก /api/area)
async function openAreaDialog(code: string) {
  const bodyEl = document.createElement("div");
  bodyEl.innerHTML = `<div class="loading-box" style="padding:20px"><div class="spinner"></div></div>`;
  openDialog({
    title: areaNames[code] ?? code,
    subtitle: "กำลังโหลด…",
    body: bodyEl,
    actions: [{ label: "ปิด", kind: "primary" }],
  });
  try {
    const d: AreaDetail = await api.area(code);
    const sub = [
      LEVEL_FULL[d.area.level],
      d.area.parent_name ?? "",
      d.children ? `${d.children} พื้นที่ย่อย` : "",
    ]
      .filter(Boolean)
      .join(" · ");

    const rows = d.metrics
      .map((m) => {
        const pct = m.of > 1 ? ((m.of - m.rank) / (m.of - 1)) * 100 : 100;
        const nf = new Intl.NumberFormat("th-TH", { maximumFractionDigits: m.decimals });
        return `
          <div style="padding:11px 0;border-bottom:1px dashed var(--border)">
            <div class="kv" style="border:none;padding:0">
              <span class="k">${m.name_th}</span>
              <span class="badge">อันดับ <strong style="font-family:var(--font-mono)">${m.rank}</strong> / ${m.of}</span>
            </div>
            <div style="font-family:var(--font-mono);font-size:21px;font-weight:600;color:var(--text-heading);margin-top:3px">${nf.format(m.value)}<span style="font-size:12px;color:var(--text-muted);font-weight:400;font-family:var(--font-thai)"> ${m.unit}</span></div>
            <div class="m-bar" style="margin-top:8px"><span style="width:${pct.toFixed(0)}%"></span></div>
          </div>`;
      })
      .join("");

    // อัปเดตหัว dialog + เนื้อหา (dialog เปิดค้างอยู่)
    const titleEl = document.querySelector<HTMLElement>(".dialog-overlay.open .dialog-title");
    const subEl = document.querySelector<HTMLElement>(".dialog-overlay.open .dialog-subtitle");
    if (titleEl) titleEl.textContent = d.area.name_th;
    if (subEl) subEl.textContent = sub;
    bodyEl.innerHTML = rows || `<p class="muted">ไม่มีข้อมูลตัวชี้วัดสำหรับพื้นที่นี้</p>`;
  } catch {
    bodyEl.innerHTML = `<p class="muted">โหลดรายละเอียดไม่ได้</p>`;
  }
}

function openAboutDialog() {
  openDialog({
    title: "เกี่ยวกับ stat",
    subtitle: "แผนที่ข้อมูล open data ประเทศไทย",
    body: `
      <img class="about-illustration" src="/ui-illustrations/about-open-data.png" alt="" aria-hidden="true" />
      <p>stat รวบรวมข้อมูลภาครัฐแบบเปิด แสดงผลเป็นแผนที่ choropleth ให้เปรียบเทียบตัวชี้วัดรายพื้นที่ได้ทันที</p>
      <ul>
        <li><strong>เลือกตัวชี้วัด</strong> จากเมนูบนแถบส่วนหัว</li>
        <li><strong>ชี้ที่พื้นที่</strong> เพื่อดูค่าแบบเรียลไทม์</li>
        <li><strong>คลิกจังหวัด → อำเภอ → ตำบล</strong> เพื่อเจาะลึกทีละระดับ</li>
        <li><strong>คลิกตำบล</strong> เพื่อดูรายละเอียดและอันดับเทียบพื้นที่พี่น้อง</li>
        <li><strong>ค้นหา</strong> ชื่อจังหวัด/อำเภอเพื่อไปยังพื้นที่นั้น</li>
      </ul>
      <p class="muted">URL ผูกกับสถานะการดู — คัดลอกลิงก์ส่งต่อได้เลย</p>
    `,
    actions: [{ label: "เริ่มใช้งาน", kind: "primary" }],
  });
}

function renderBreadcrumb() {
  const s = store.get();
  const parts: string[] = [];
  if (s.level === "province") {
    parts.push(`<span class="here">${THAILAND_FLAG}ประเทศไทย</span>`);
  } else if (s.level === "amphoe" && s.area) {
    parts.push(`<a href="#" data-nav="national">${THAILAND_FLAG}ประเทศไทย</a>`);
    parts.push(`<span class="here">${areaNames[s.area] ?? "จังหวัด"}</span>`);
  } else if (s.level === "tambon" && s.area) {
    const pro = s.area.slice(0, 2);
    parts.push(`<a href="#" data-nav="national">${THAILAND_FLAG}ประเทศไทย</a>`);
    parts.push(`<a href="#" data-nav="prov:${pro}">${areaNames[pro] ?? "จังหวัด"}</a>`);
    parts.push(`<span class="here">${areaNames[s.area] ?? "อำเภอ"}</span>`);
  }
  breadcrumbEl.innerHTML = parts.join(' <span class="sep">›</span> ');
  breadcrumbEl.querySelectorAll<HTMLElement>("[data-nav]").forEach((el) =>
    el.addEventListener("click", (e) => {
      e.preventDefault();
      const nav = el.dataset.nav!;
      if (nav === "national") store.set({ level: "province", area: null });
      else if (nav.startsWith("prov:")) store.set({ level: "amphoe", area: nav.slice(5) });
    }),
  );
}

// การ์ดสรุปบน side pane
function renderSummary(choro: Choropleth, level: Level) {
  const s = store.get();
  const ind = currentIndicator();
  const n = Object.keys(choro.values).length;
  const drilled = level !== "province";
  const drillLevel = nextLevel(level);
  const canDrill = drillLevel ? supportsLevel(ind, drillLevel) : true;
  const hint =
    !canDrill
      ? `ข้อมูลนี้มีถึงระดับ${LEVEL_NOUN[level]}`
      : level === "province"
      ? "ชี้ที่พื้นที่เพื่อดูค่า · คลิกจังหวัดเพื่อเจาะรายอำเภอ"
      : level === "amphoe"
        ? "คลิกอำเภอเพื่อเจาะรายตำบล"
        : "คลิกตำบลเพื่อดูรายละเอียด + อันดับ";
  summaryEl.innerHTML = `
    <div class="card">
      <p class="card-title">${ind?.name_th ?? "—"}</p>
      <div style="display:flex;gap:6px;flex-wrap:wrap;margin:8px 0 2px">
        <span class="badge badge-ink">ราย${LEVEL_NOUN[level]}</span>
        <span class="badge">ปี ${s.period ?? "—"}</span>
        ${drilled ? `<span class="badge badge-accent">${areaNames[s.area!] ?? ""}</span>` : ""}
      </div>
      <div class="detail-stat">
        <span class="stat-value">${n}</span>
        <span class="stat-label">พื้นที่ที่มีข้อมูล${level === "province" ? " (จาก 77 จังหวัด)" : ""}</span>
      </div>
      ${drilled ? `<button type="button" class="btn btn-outline btn-sm" id="back-up">${ICON_BACK} ย้อนกลับ</button>` : ""}
      <p class="muted" style="margin:10px 0 0">${hint}</p>
    </div>
  `;
  summaryEl.querySelector("#back-up")?.addEventListener("click", () => {
    if (level === "tambon") store.set({ level: "amphoe", area: s.area!.slice(0, 2) });
    else store.set({ level: "province", area: null });
  });
}

function renderLegend(choro: Choropleth, fmt: (v: number) => string) {
  const colors = seqColors();
  const bins = choro.meta.bins ?? [];
  if (!bins.length) {
    legendEl.innerHTML = "";
    legendEl.style.display = "none";
    return;
  }
  legendEl.style.display = "";
  const edges = [choro.meta.min, ...bins, choro.meta.max];
  const rows = colors
    .map((c, i) => {
      if (edges[i] == null || edges[i + 1] == null) return "";
      return `<div class="legend-row"><span class="sw" style="background:${c}"></span><span class="val">${fmt(edges[i])} – ${fmt(edges[i + 1])}</span></div>`;
    })
    .join("");
  legendEl.innerHTML = `<div class="legend-title">ช่วงค่า</div>${rows}`;
}

// ค้นหาพื้นที่แบบ autocomplete → ให้ผู้ใช้เลือกพื้นที่เองก่อน drill
let searchTimer: number | undefined;
let searchRequest = 0;
let searchHits: SearchHit[] = [];
let activeSearchIndex = -1;

const LEVEL_LABEL: Record<Level, string> = { province: "จังหวัด", amphoe: "อำเภอ/เขต", tambon: "ตำบล/แขวง" };

function clearSearchResults() {
  searchHits = [];
  activeSearchIndex = -1;
  searchResultsEl.replaceChildren();
  searchResultsEl.classList.remove("open");
  searchInput.setAttribute("aria-expanded", "false");
  searchInput.removeAttribute("aria-activedescendant");
}

function setActiveSearchResult(index: number) {
  if (!searchHits.length) return;
  activeSearchIndex = (index + searchHits.length) % searchHits.length;
  searchResultsEl.querySelectorAll<HTMLElement>(".search-result").forEach((result, i) => {
    const active = i === activeSearchIndex;
    result.classList.toggle("is-active", active);
    result.setAttribute("aria-selected", String(active));
  });
  searchInput.setAttribute("aria-activedescendant", `search-result-${activeSearchIndex}`);
}

function chooseSearchHit(hit: SearchHit) {
  areaNames[hit.area_code] = hit.name_th;
  searchInput.value = "";
  clearSearchResults();
  searchInput.blur();
  if (hit.level === "province") store.set({ level: "amphoe", area: hit.area_code });
  else if (hit.level === "amphoe") store.set({ level: "tambon", area: hit.area_code });
  else openAreaDialog(hit.area_code);
}

function renderSearchResults(hits: SearchHit[]) {
  searchHits = hits;
  activeSearchIndex = -1;
  searchResultsEl.replaceChildren();
  if (!hits.length) {
    const empty = document.createElement("p");
    empty.className = "search-empty";
    empty.textContent = "ไม่พบพื้นที่ที่ตรงกับคำค้น";
    searchResultsEl.appendChild(empty);
  } else {
    hits.forEach((hit, index) => {
      const result = document.createElement("button");
      result.type = "button";
      result.id = `search-result-${index}`;
      result.className = "search-result";
      result.setAttribute("role", "option");
      result.setAttribute("aria-selected", "false");

      const top = document.createElement("span");
      top.className = "search-result-top";
      const name = document.createElement("strong");
      name.textContent = hit.name_th;
      const level = document.createElement("span");
      level.className = "search-result-level";
      level.textContent = LEVEL_LABEL[hit.level];
      top.append(name, level);

      const path = document.createElement("span");
      path.className = "search-result-path";
      path.textContent = hit.path;
      result.append(top, path);
      result.addEventListener("mousedown", (event) => event.preventDefault());
      result.addEventListener("click", () => chooseSearchHit(hit));
      searchResultsEl.appendChild(result);
    });
  }
  searchResultsEl.classList.add("open");
  searchInput.setAttribute("aria-expanded", "true");
}

searchInput.addEventListener("input", () => {
  window.clearTimeout(searchTimer);
  const request = ++searchRequest;
  searchTimer = window.setTimeout(async () => {
    const q = searchInput.value.trim();
    if (q.length < 2) {
      clearSearchResults();
      return;
    }
    try {
      const hits = await api.search(q);
      if (request !== searchRequest) return;
      renderSearchResults(hits);
    } catch (e) {
      if (request !== searchRequest) return;
      console.error(e);
      clearSearchResults();
      toast("ค้นหาไม่สำเร็จ", "error");
    }
  }, 300);
});

searchInput.addEventListener("keydown", (event) => {
  if (event.key === "ArrowDown") {
    event.preventDefault();
    setActiveSearchResult(activeSearchIndex + 1);
  } else if (event.key === "ArrowUp") {
    event.preventDefault();
    setActiveSearchResult(activeSearchIndex - 1);
  } else if (event.key === "Enter" && activeSearchIndex >= 0) {
    event.preventDefault();
    chooseSearchHit(searchHits[activeSearchIndex]);
  } else if (event.key === "Escape") {
    clearSearchResults();
  }
});

searchInput.addEventListener("blur", () => {
  searchRequest++;
  window.setTimeout(clearSearchResults, 150);
});

indicatorSelect.addEventListener("change", () => {
  const code = indicatorSelect.value;
  if (!code) {
    store.set({ indicator: null, period: null, level: "province", area: null });
    return;
  }
  selectDataset(code);
});

yearSelect.addEventListener("change", () => {
  const y = Number(yearSelect.value);
  if (y) store.set({ period: y });
});

document.getElementById("about-btn")!.addEventListener("click", openAboutDialog);

store.subscribe(() => renderView());

let resizeTimer: number | undefined;
window.addEventListener("resize", () => {
  window.clearTimeout(resizeTimer);
  resizeTimer = window.setTimeout(() => renderView(), 200);
});

loadIndicators()
  .catch(() => toast("โหลดรายการตัวชี้วัดไม่สำเร็จ", "error"))
  .then(() => renderView());
app.setAttribute("aria-busy", "false");
